import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';

import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { Right } from './dtos/right';
import { User } from './dtos/user';
import { UserDetails } from './dtos/userDetails';
import { UserEmailTemplate } from './dtos/userEmailTemplate';
import { UserEmailTemplateForm } from './dtos/userEmailTemplateForm';

@Injectable({ providedIn: 'root' })
export class CustomerUsersService {

  private usersUrl = this.baseUrl + '/api/users';

  public constructor(
    private http: HttpClient,
    private toastr: ToastrService,
    @Inject('API_BASE_URL') private baseUrl: string) { }

  public getUserDetails(): Observable<UserDetails> {
    return this.http.get<UserDetails>(this.usersUrl + '/GetUserDetails')
      .pipe(
        catchError(this.handleError<UserDetails>('GetUserDetails'))
      );
  }

  public getAllRights(): Observable<Right[]> {
    return this.http.get<Right[]>(this.usersUrl + '/GetAllRights')
      .pipe(
        catchError(this.handleError<Right[]>('getAllRights'))
      );
  }

  public getAll(): Observable<User[]> {
    return this.http.get<User[]>(this.usersUrl + '/GetAll')
      .pipe(
        catchError(this.handleError<User[]>('getAll'))
      );
  }

  public getAdminOnly(): Observable<User[]> {
    return this.http.get<User[]>(this.usersUrl + '/GetAdminOnly')
      .pipe(
        catchError(this.handleError<User[]>('getAdminOnly'))
      );
  }

  public addCustomerUser(customerId: string, userToAdd: User): Observable<string> {
    return this.http.post<string>(this.usersUrl + '/AddCustomerUser/' + customerId, userToAdd)
      .pipe(
        tap(() => this.toastr.success('Saved Sucessfully')),
        catchError(this.handleError<string>('addUser'))
      );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public updateCustomerUser(userToUpdate: User): Observable<any> {
    return this.http.put(this.usersUrl + '/UpdateCustomerUser/' + userToUpdate.id, userToUpdate)
      .pipe(
        tap(() => this.toastr.success('Saved Sucessfully')),
        catchError(this.handleError<User>('updateUser'))
      );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public deleteCustomerUser(customerId: string, userId: string): Observable<any> {
    return this.http.delete(this.usersUrl + '/DeleteCustomerUser/' + customerId + '/' + userId)
      .pipe(
        tap(() => this.toastr.success('Saved Sucessfully')),
        catchError(this.handleError<User>('deleteUser'))
      );
  }

  public addUser(userToAdd: User): Observable<string> {
    return this.http.post<string>(this.usersUrl + '/Add', userToAdd)
      .pipe(
        tap(() => this.toastr.success('Saved Sucessfully')),
        catchError(this.handleError<string>('addUser'))
      );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public updateUser(userToUpdate: User): Observable<any> {
    return this.http.put(this.usersUrl + '/Update/' + userToUpdate.id, userToUpdate)
      .pipe(
        tap(() => this.toastr.success('Saved Sucessfully')),
        catchError(this.handleError<User>('updateUser'))
      );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public deleteUser(userId: string): Observable<any> {
    return this.http.delete(this.usersUrl + '/Delete/' + userId)
      .pipe(
        tap(() => this.toastr.success('Deleted Sucessfully')),
        catchError(this.handleError<User>('deleteUser'))
      );
  }

  public addUserEmailTemplate(userEmailTemplate: UserEmailTemplateForm): Observable<string> {
    return this.http.post<string>(this.usersUrl + '/AddUserEmailTemplate', userEmailTemplate)
      .pipe(
        tap(() => this.toastr.success('Saved Sucessfully')),
        catchError(this.handleError<string>('addUserEmailTemplate'))
      );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public updateUserEmailTemplate(userEmailTemplate: UserEmailTemplateForm): Observable<any> {
    return this.http.put(this.usersUrl + '/UpdateUserEmailTemplate/' + userEmailTemplate.id, userEmailTemplate)
      .pipe(
        tap(() => this.toastr.success('Saved Sucessfully')),
        catchError(this.handleError<User>('updateUserEmailTemplate'))
      );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public deleteUserEmailTemplate(userTemplateId: string, userId: string, customerId?: string): Observable<any> {
    return this.http.delete(this.usersUrl + '/DeleteUserEmailTemplate/' + userTemplateId + '/' + userId + '?customerId=' + customerId)
      .pipe(
        tap(() => this.toastr.success('Saved Sucessfully')),
        catchError(this.handleError<User>('deleteUserEmailTemplate'))
      );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public getUserEmailTemplates(userId: string, customerId?: string): Observable<UserEmailTemplate[]> {
    return this.http.get<UserEmailTemplate[]>(this.usersUrl + '/GetUserEmailTemplates/' + userId + '?customerId=' + customerId)
      .pipe(
        catchError(this.handleError<UserEmailTemplate[]>('getUserEmailTemplates'))
      );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public verifyAdminUserEmailUse(payload: { email: string; isAdminPanel: boolean }): Observable<boolean> {
    return this.http.get<boolean>(this.usersUrl + '/ValidateEmailUseAdminOnly/' + payload.email + '/' + payload.isAdminPanel)
      .pipe(
        catchError(this.handleError<boolean>('verifyAdminUserEmailUse'))
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars
  private handleError<T>(operation = 'operation', result?: T | undefined): (err: any) => Observable<T> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (error: any): Observable<T> => {

      console.error('An error occurred', error);
      this.toastr.error(`${operation} failed: ${error.message}`);

      throw error;
    };
  }
}
